// Considered: SphereSearch — rejected because it is a dropdown with no way to exclude this sphere or mark a destination
// that already rolls up; PickerShellMolecule gives the listbox, the search and the empty/error states. See spec Reuse Audit.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import PickerShellMolecule from '../PickerShell/PickerShellMolecule';
import { nonBlank } from '../../../utils/nonBlank';

export interface PickableMeasure {
  id: string;
  name: string;
}

export interface MeasurePickerProps {
  /** The sphere that would roll up; never offered as its own destination. */
  memberId: string;
  /** Destinations the card already lists and that count this sphere in (not excluded): they open view mode. */
  alreadyRollsUpInto: string[];
  onPick: (measure: PickableMeasure) => void;
  onCancel: () => void;
}

interface SearchResponse {
  spheres?: Array<{ id: string; properties?: { name?: string } }>;
}

/** Keystrokes inside this window collapse into one search request. */
const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 100;

const MeasurePicker: React.FC<MeasurePickerProps> = ({ memberId, alreadyRollsUpInto, onPick, onCancel }) => {
  const { t } = useTranslation();
  const graphClient = useApiClientsOrNull()?.graphClient;
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<PickableMeasure[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const term = query.trim();
    if (term === searchTerm) return undefined;
    const timer = setTimeout(() => setSearchTerm(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, searchTerm]);

  useEffect(() => {
    if (!graphClient) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    graphClient
      .post<SearchResponse>('/search', {
        searchTerm,
        page: 1,
        limit: PAGE_SIZE,
        filters: [{ property: 'measureRole', value: 'aggregate' }],
        filterOperator: 'and',
      })
      .then((response) => {
        if (cancelled) return;
        setItems(
          (response?.spheres ?? [])
            .filter((sphere) => sphere.id !== memberId)
            .map((sphere) => ({ id: sphere.id, name: nonBlank(sphere.properties?.name) ?? sphere.id }))
        );
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [graphClient, memberId, searchTerm, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return (
    <PickerShellMolecule<PickableMeasure>
      items={items}
      query={query}
      onQueryChange={setQuery}
      filterItems={false}
      getSearchText={(item) => item.name}
      getRowKey={(item) => item.id}
      renderRow={(item) => (
        <span className="measure-picker__row">
          <span className="measure-picker__name">{item.name}</span>
          {alreadyRollsUpInto.includes(item.id) && (
            <span className="measure-picker__note">{t('statusFactors.upward.picker.already')}</span>
          )}
        </span>
      )}
      onPickItem={onPick}
      onCancel={onCancel}
      cancelLabel={t('statusFactors.upward.picker.cancel')}
      listLabel={t('statusFactors.upward.picker.label')}
      searchPlaceholder={t('statusFactors.upward.picker.search')}
      loading={loading}
      renderLoading={() => <SkeletonAtom height="2.5rem" count={3} />}
      errorMessage={failed ? t('statusFactors.upward.picker.error') : null}
      onRetry={retry}
      retryLabel={t('statusFactors.upward.retry')}
      noMatchesMessage={
        searchTerm
          ? t('statusFactors.upward.picker.noMatch', { query: searchTerm })
          : t('statusFactors.upward.picker.empty')
      }
    />
  );
};

export default MeasurePicker;
